import {
  EeClass,
  ElectionFlow,
  EstimatedCostStatistics,
  ProductInfo,
  RateStatistic,
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  StatisticsValues,
  Subscriber,
} from 'types/electionService';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import {
  getEmployeeClasses,
  getProducts,
  getRequiredSubInfo,
  getStatistics,
  getSubscriberWithChoices,
} from 'api/operations/electionService';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { getClassesAvailableForSelection } from 'global/services-helpers/electionService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { getCart } from 'api/operations/cartServiceV2';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { isSubscriberValid as isSubscriberValidForFlow } from 'global/utils/election/subscriberValidation';
import { bpHistory } from 'global/bpHistory';
import { SubscriberPersonInfo } from 'store/modules/subscriber/subscriber-basic-info/types';
import SubscriberElectionsDelegate from './subscriberElectionsDelegate';
import ProductLineRateModalDelegate from './productLineRateModalDelegate';
import { SubscriberPresenter } from './types';

export const INITIAL_STATISTICS: StatisticsValues = {
  total: 0,
  elected: 0,
  electionsMissing: 0,
  waived: 0,
  invited: 0,
  registered: 0,
  estimatedCost: {} as EstimatedCostStatistics,
};

interface DomainData {
  groupId: string;
  enrollmentPeriodId: string;
  cartId?: string;
  /**
   * Arguments for creating an `ElectionsModule`.
   *
   * `currentFlow` (optional, default: `enrollment`):
   * The API provides both enrollment and quoting profile rules.
   * electionPresenter uses this to pick form ids and required fields for the active flow .
   */
  currentFlow?: ElectionFlow;
}

export default class ElectionsModule {
  private _rootStore = getRootStoreInstance();
  private _domainData: DomainData;

  private _statistics: StatisticsValues = INITIAL_STATISTICS;
  private _rateStats: RateStatistic | null = null;
  private _products: ProductInfo[] = [];
  private _classes: EeClass[] = [];
  private _requiredSubInfo: RequiredSubInfo | null = null;
  private _subscribers: SubscriberElectionsDelegate[] = [];
  private _domainProductLineIds: ProductLineId[] = [];
  public productLineRateModalDelegate: ProductLineRateModalDelegate;

  public get domainData(): DomainData {
    return this._domainData;
  }

  public get enrollmentPeriodId(): string {
    return this._domainData.enrollmentPeriodId;
  }

  public get rateStats(): RateStatistic | null {
    return this._rateStats;
  }

  public get domainProductLineIds(): ProductLineId[] {
    return this._domainProductLineIds;
  }

  /**
   * The flow used to create the required sub info presenter.
   */
  public get electionFlow(): ElectionFlow {
    return this._domainData.currentFlow || 'enrollment';
  }

  /**
   * Required subscriber rules for the active flow (see {@link electionFlow}):
   * enrollment uses `editSubFormId` / `requiredFields`;
   * quoting uses `quotingSubFormId` / `quotingRequiredFields`;
   * Dependents form and `productLineRequiredInfo` follow {@link RequiredSubInfo}.
   */
  public get electionPresenter(): RequiredSubInfoPresenter | null {
    return createRequiredSubInfoPresenter(toJS(this._requiredSubInfo), this.electionFlow);
  }

  public get requiredSubInfo(): RequiredSubInfo | null {
    return this._requiredSubInfo;
  }

  /**
   * Full list of classes for the period/group.
   */
  public get classes(): EeClass[] {
    return this._classes;
  }

  /**
   * Subset of classes allowed for selection (filtered by domain availableClasses).
   * Use for SubscriberCard so only available classes are shown in the class dropdown.
   */
  public get classesAvailableForSelection(): EeClass[] {
    return getClassesAvailableForSelection(this.classes, this._rootStore.domainStoreV2.availableClasses);
  }

  public get statistics(): StatisticsValues {
    return this._statistics;
  }

  public get products(): ProductInfo[] {
    return this._products;
  }

  public get subscribers(): SubscriberElectionsDelegate[] {
    return this._subscribers;
  }

  public isSubscriberValid(subscriber: SubscriberPresenter): boolean {
    return (
      this.electionPresenter !== null && isSubscriberValidForFlow(this.electionPresenter, subscriber, this.electionFlow)
    );
  }

  public redirectToPortfolioDetails(
    productLineId: ProductLineId,
    employeeClass: string,
    additionalQueryParams?: string,
  ): void {
    const currentProductLinePortfolioId =
      this._products.find((product) => product.productLineId === productLineId)?.portfolioId || '';

    if (!currentProductLinePortfolioId) {
      throw new Error('Current product line portfolio id is not found');
    }

    const cartId = this._domainData.cartId;
    if (!cartId) {
      throw new Error('Cart id is not found');
    }

    const link = `/product-compare?type=available-products&portfolioIds=${currentProductLinePortfolioId}&cartId=${cartId}&productLineId=${productLineId}&periodId=${
      this._domainData.enrollmentPeriodId
    }&eeClass=${employeeClass}${additionalQueryParams ? `&${additionalQueryParams}` : ''}`;

    bpHistory.push(link);
  }

  public async openProductLineRateForm(
    formId: string,
    employeeClass: string,
    common: SubscriberPersonInfo,
    productId: string,
    saveAnswers: (answers: Record<string, string>) => Promise<void>,
  ): Promise<void> {
    await this.productLineRateModalDelegate.openAndFetchForm(
      {
        employeeClass,
        common,
        formId,
        productId,
      },
      saveAnswers,
    );
  }

  public async getStatistics(): Promise<void> {
    const res = await getStatistics(this._domainData.groupId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      const { rateStats, ...restStatistic } = res;
      this._statistics = restStatistic;
      this._rateStats = rateStats;
    });
  }

  async getProducts(): Promise<void> {
    const products = await getProducts(this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._products = products;
    });
  }

  async getClasses(): Promise<void> {
    const classes = await getEmployeeClasses(this._domainData.groupId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._classes = classes;
    });
  }

  async getRequiredSubInfo(): Promise<void> {
    const requiredSubInfo = await getRequiredSubInfo(this._rootStore.domainStoreV2.domainHeader.id);
    runInAction(() => {
      this._requiredSubInfo = requiredSubInfo;
    });
  }

  public async getProductLines(): Promise<void> {
    const cartId = this._domainData.cartId;
    if (!cartId) {
      return;
    }

    const agencyId = this._rootStore.userStore.imAgencyId;
    const sponsorId = this._rootStore.userStore.sponsorId;

    const cart = await getCart(cartId);
    const { effectiveDate, zipCode, state, county, sic4 } = cart.lastUpdateBrowsingCriteria;

    const productLines = await getDomainProductLine({
      effectiveDate,
      zipCode,
      state,
      county,
      sic: sic4,
      agencyId,
      sponsorId,
    });

    runInAction(() => {
      this._domainProductLineIds = productLines
        .filter((productLine) => productLine.rateSegment === cart.rateSegment)
        .map((productLine) => productLine.productLineId)
        .sort(sortByProductLineId);
    });
  }

  public async fetchSubscriberById(subscriberId: string): Promise<Pick<Subscriber, 'id' | 'common' | 'custom'>> {
    const subscriber = await getSubscriberWithChoices(subscriberId, this._domainData.enrollmentPeriodId);
    runInAction(() => {
      this._subscribers = [
        SubscriberElectionsDelegate.fromSubscriber(subscriber, {
          periodId: this._domainData.enrollmentPeriodId,
          products: this.products,
          rateStats: this.rateStats,
          domainProductLineIds: this.domainProductLineIds,
          refreshSubscriber: (): Promise<Subscriber> =>
            getSubscriberWithChoices(subscriberId, this._domainData.enrollmentPeriodId),
          refreshStatistics: (): Promise<void> => this.getStatistics(),
        }),
      ];
    });
    return subscriber;
  }

  public resetSubscribers(): void {
    this._subscribers = [];
  }

  public setCartId(cartId: string): void {
    this._domainData = { ...this._domainData, cartId };
  }

  constructor(params: DomainData) {
    this._domainData = {
      groupId: params.groupId,
      enrollmentPeriodId: params.enrollmentPeriodId,
      currentFlow: params.currentFlow || 'enrollment',
      cartId: params.cartId,
    };
    this.productLineRateModalDelegate = new ProductLineRateModalDelegate();

    makeObservable<
      ElectionsModule,
      | '_domainData'
      | '_statistics'
      | '_rateStats'
      | '_products'
      | '_classes'
      | '_requiredSubInfo'
      | '_subscribers'
      | '_domainProductLineIds'
    >(this, {
      _domainData: observable,
      _statistics: observable,
      _rateStats: observable,
      _products: observable,
      _classes: observable,
      _requiredSubInfo: observable,
      _subscribers: observable,
      _domainProductLineIds: observable,
      requiredSubInfo: computed,
      rateStats: computed,
      electionFlow: computed,
      electionPresenter: computed,
      domainData: computed,
      classes: computed,
      classesAvailableForSelection: computed,
      statistics: computed,
      products: computed,
      subscribers: computed,
      domainProductLineIds: computed,
      productLineRateModalDelegate: observable,
      getStatistics: action.bound,
      getProducts: action.bound,
      getClasses: action.bound,
      getRequiredSubInfo: action.bound,
      getProductLines: action.bound,
      fetchSubscriberById: action.bound,
      resetSubscribers: action.bound,
      setCartId: action.bound,
      redirectToPortfolioDetails: action.bound,
      openProductLineRateForm: action.bound,
    });
  }
}
